import { Delete } from "@mui/icons-material";
import Edit from "@mui/icons-material/Edit";
import _ from "lodash";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { ReactElement, useCallback, useMemo, useState } from "react";

import {
  DeleteShortCodeOptInKeywordMutation,
  DeleteShortCodeOptInKeywordMutationVariables,
} from "~frontend/__generated__/graphql";
import Table from "~frontend/components/Table";
import Copy from "~frontend/components/icons/Copy";
import {
  DELETE_SHORT_CODE_OPT_IN_KEYWORD,
  LIST_SHORT_CODE_OPT_IN_KEYWORDS,
} from "~frontend/graphql/shortcode";
import { formatShortDateTime } from "~frontend/util/dateFormat";
import useMutation from "~frontend/util/useMutation";

import { ShortcodeOptInKeyword } from "./types";

export default function OptInKeywordsTable({
  keywords,
  shortcodeId,
  openEditDialog,
  openCopyKeywordLinkDialog,
}: {
  keywords: ShortcodeOptInKeyword[];
  shortcodeId: string;
  openEditDialog: (keyword: ShortcodeOptInKeyword) => void;
  openCopyKeywordLinkDialog: (keyword: ShortcodeOptInKeyword) => void;
}): ReactElement {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const snackbar = useSnackbar();

  const [deleteKeyword] = useMutation<
    DeleteShortCodeOptInKeywordMutation,
    DeleteShortCodeOptInKeywordMutationVariables
  >(DELETE_SHORT_CODE_OPT_IN_KEYWORD, {
    refetchQueries: [
      {
        query: LIST_SHORT_CODE_OPT_IN_KEYWORDS,
        variables: { input: { shortcodeId } },
      },
    ],
    awaitRefetchQueries: true,
  });

  const handleDeleteKeyword = useCallback(
    async (keyword: ShortcodeOptInKeyword) => {
      const { errors } = await deleteKeyword({
        variables: { input: { shortCodeOptInKeywordId: keyword.id } },
      });

      if (errors) {
        snackbar.enqueueSnackbar(
          `Error deleting keyword: ${errors[0].message}`,
          {
            variant: "error",
          }
        );
        throw errors;
      } else {
        snackbar.enqueueSnackbar(
          `Keyword ${keyword.normalizedKeyword} deleted`
        );
      }
    },
    [snackbar, deleteKeyword]
  );

  const rows = useMemo(() => {
    const sorted = _.sortBy(keywords, keyword => keyword.normalizedKeyword);
    return sorted.slice(page * pageSize, (page + 1) * pageSize);
  }, [keywords, page, pageSize]);

  return (
    <Table
      columns={[
        {
          key: "keyword",
          title: "Keyword",
          renderValue: keyword => keyword.normalizedKeyword,
        },
        {
          key: "message",
          title: "Additional Message",
          renderValue: keyword => keyword.additionalWelcomeMessage || "-",
          align: "left",
          sx: { maxWidth: "400px" },
        },
        {
          key: "created",
          title: "Created",
          renderValue: keyword => (
            <>
              {formatShortDateTime(DateTime.fromMillis(keyword.createdAt))}, by{" "}
              {keyword.createdByUser.fullName}
              {keyword.updatedAt > keyword.createdAt && (
                <>
                  <br />
                  Updated{" "}
                  {formatShortDateTime(DateTime.fromMillis(keyword.updatedAt))},
                  by {keyword.updatedByUser.fullName}
                </>
              )}
            </>
          ),
          align: "left",
        },
      ]}
      rows={rows}
      totalCount={keywords.length}
      rowsPerPage={pageSize}
      onChangeRowsPerPage={setPageSize}
      page={page}
      onChangePage={setPage}
      rowActions={keyword => [
        {
          id: "copy",
          icon: <Copy />,
          tooltipLabel: "Copy link",
          onClick: () => openCopyKeywordLinkDialog(keyword),
        },
        {
          id: "edit",
          icon: <Edit />,
          tooltipLabel: "Edit",
          onClick: () => openEditDialog(keyword),
        },
        {
          id: "delete",
          icon: <Delete />,
          tooltipLabel: "Delete",
          confirm: {
            title: "Delete Keyword",
            content: `Are you sure you want to delete the keyword "${keyword.normalizedKeyword}"?`,
          },
          onClick: () => handleDeleteKeyword(keyword),
        },
      ]}
      emptyState="No keywords defined for this shortcode."
    />
  );
}
